import { getAnalytics, isSupported } from "firebase/analytics";
import { getApp, getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, initializeFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, type Functions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { firebaseConfig } from "../firebaseConfig";

type FirebaseClientGlobalCache = {
  app?: ReturnType<typeof getApp>;
  db?: ReturnType<typeof getFirestore>;
  dbId?: string;
  useEmulators?: boolean;
  firestoreEmulatorConnected?: boolean;
  functions?: Functions | null;
  functionsEmulatorConnected?: boolean;
};

const globalCache = (() => {
  const g = globalThis as unknown as { __ezRB_FIREBASE_CLIENT__?: FirebaseClientGlobalCache };
  if (!g.__ezRB_FIREBASE_CLIENT__) {
    g.__ezRB_FIREBASE_CLIENT__ = {};
  }
  return g.__ezRB_FIREBASE_CLIENT__!;
})();

const app = (() => {
  if (globalCache.app) {
    return globalCache.app;
  }
  const instance = getApps().length ? getApp() : initializeApp(firebaseConfig);
  globalCache.app = instance;
  return instance;
})();

// Determine if we should use emulators (must be done before any operations)
const forceEmulator = (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR ?? "").toLowerCase() === "true";
const disableEmulators = (import.meta.env.VITE_DISABLE_EMULATORS ?? "").toLowerCase() === "true";

const shouldUseEmulator = (hostname: string): boolean => {
  // Allow explicitly disabling emulators (useful for local dev against real backend)
  if (disableEmulators) {
    return false;
  }

  if (forceEmulator) {
    return true;
  }

  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]" ||
    hostname === "0.0.0.0" ||
    hostname.endsWith(".local") ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname.startsWith("172.16.") ||
    hostname.startsWith("172.17.") ||
    hostname.startsWith("172.18.") ||
    hostname.startsWith("172.19.") ||
    hostname.startsWith("172.2")
  );
};

// Get hostname at runtime to determine if we should use emulators
function getHostnameSync(): string {
  if (typeof window !== "undefined" && window.location) {
    return window.location.hostname;
  }
  return "";
}

// Function to determine if we should use emulators (called at runtime)
function shouldUseEmulatorsNow(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const hostname = getHostnameSync();
  return shouldUseEmulator(hostname);
}

// Function to get the correct database ID (called at runtime)
function getDatabaseId(): string {
  return shouldUseEmulatorsNow() ? "(default)" : (firebaseConfig.databaseId as string);
}

// Initialize services
const auth = getAuth(app);
const storage = getStorage(app);

// Function to connect to emulators (called at module load and can be called again)
function connectToEmulatorsInternal(): void {
  if (typeof window === "undefined") {
    return;
  }

  const useEmulators = shouldUseEmulatorsNow();
  const hostname = getHostnameSync();
  
  console.log(`%c[Firebase Client] Checking emulator connection (hostname: ${hostname}, useEmulators: ${useEmulators})`, "color: #FF9800");

  if (!useEmulators) {
    console.log("%c[Firebase Client] Not using emulators (production mode)", "color: #F44336");
    return;
  }

  // Connect Auth emulator
  try {
    connectAuthEmulator(auth, `http://127.0.0.1:9099`, { disableWarnings: true });
    console.log("%c[Firebase Client] ✓ Auth emulator connected", "color: #4CAF50");
  } catch (error) {
    if (error instanceof Error && !error.message.includes("already been initialized")) {
      console.error("[Firebase Client] ✗ Failed to connect to Auth emulator", error);
    }
  }

  // Connect Storage emulator
  try {
    connectStorageEmulator(storage, "127.0.0.1", 9199);
    console.log("%c[Firebase Client] ✓ Storage emulator connected", "color: #4CAF50");
  } catch (error) {
    if (error instanceof Error && !error.message.includes("already been initialized")) {
      console.error("[Firebase Client] ✗ Failed to connect to Storage emulator", error);
    }
  }
}

// Connect to emulators at module load
connectToEmulatorsInternal();

// Initialize Firestore with correct database ID (always check at runtime)
// This ensures we use the right database even if the module was cached
const db = (() => {
  const databaseId = getDatabaseId();
  const useEmulators = typeof window !== "undefined" && shouldUseEmulatorsNow();

  // Reuse the same Firestore instance across HMR/Turbopack reloads to avoid
  // watch-stream state corruption (e.g., INTERNAL ASSERTION FAILED: ca9).
  if (globalCache.db && globalCache.dbId === databaseId && globalCache.useEmulators === useEmulators) {
    return globalCache.db;
  }

  let instance: ReturnType<typeof getFirestore>;

  if (useEmulators) {
    // Long polling is slower but much more robust for local emulators + HMR.
    try {
      instance = initializeFirestore(
        app,
        {
          experimentalForceLongPolling: true,
          // Prefer long polling in dev/emulators to reduce flakiness under HMR / listener churn.
          // (Some Firestore builds also support additional streaming toggles, but we keep this type-safe.)
          experimentalAutoDetectLongPolling: true,
        },
        databaseId,
      );
    } catch {
      instance = getFirestore(app, databaseId);
    }
  } else {
    instance = getFirestore(app, databaseId);
  }

  globalCache.db = instance;
  globalCache.dbId = databaseId;
  globalCache.useEmulators = useEmulators;
  return instance;
})();

// Connect Firestore emulator AFTER Firestore is initialized
if (typeof window !== "undefined" && shouldUseEmulatorsNow()) {
  try {
    // Only connect once per browser session, even if modules are reloaded.
    if (!globalCache.firestoreEmulatorConnected) {
      connectFirestoreEmulator(db, "127.0.0.1", 8081);
      globalCache.firestoreEmulatorConnected = true;
    }
    const dbId = getDatabaseId();
    const hostname = getHostnameSync();
    console.log("%c[Firebase Client] ✓ Firestore emulator connected at 127.0.0.1:8081", "color: #4CAF50");
    console.log(`%c[Firebase Client] Using database ID: ${dbId} (hostname: ${hostname})`, "color: #2196F3");
    
    // Runtime verification: Check if we're actually using the emulator
    // This helps detect if the module was cached with wrong settings
    if (dbId !== "(default)") {
      console.warn(
        "%c[Firebase Client] ⚠️ WARNING: Expected database ID '(default)' for emulators, but got:",
        "color: #FF9800; font-weight: bold",
        dbId
      );
      console.warn(
        "%c[Firebase Client] This may indicate the module was cached. Try a hard refresh (Ctrl+Shift+R).",
        "color: #FF9800"
      );
    }
  } catch (error) {
    if (error instanceof Error && !error.message.includes("already been initialized")) {
      console.error("[Firebase Client] ✗ Failed to connect to Firestore emulator", error);
      console.error("[Firebase Client] Error details:", error);
    }
  }
} else if (typeof window !== "undefined") {
  // Log when NOT using emulators (production mode)
  const hostname = getHostnameSync();
  const dbId = getDatabaseId();
  console.log(`%c[Firebase Client] Production mode (hostname: ${hostname}, database ID: ${dbId})`, "color: #F44336");
}

async function loadAnalytics() {
  if (typeof window === "undefined") {
    return null;
  }

  const supported = await isSupported();
  return supported ? getAnalytics(app) : null;
}

// Lazy-load analytics to avoid top-level await issues with Turbopack
// Top-level await causes chunk loading errors in Turbopack
let analyticsPromise: Promise<ReturnType<typeof getAnalytics> | null> | null = null;
const getAnalyticsInstance = async () => {
  if (!analyticsPromise) {
    analyticsPromise = loadAnalytics();
  }
  return analyticsPromise;
};

// Export as a Promise to avoid blocking module load
// Consumers should await this if they need analytics
const analytics = typeof window !== "undefined" ? getAnalyticsInstance() : Promise.resolve(null);

const googleProvider = new GoogleAuthProvider();
// Request profile and email scopes
googleProvider.addScope("profile");
googleProvider.addScope("email");
// Always show account selector to allow users to choose which Google account to use
googleProvider.setCustomParameters({ prompt: "select_account" });

const appleProvider = new OAuthProvider("apple.com");
// Request email and name scopes
appleProvider.addScope("email");
appleProvider.addScope("name");

let cachedFunctions: Functions | null = null;
let hasConnectedToEmulator = false;

const emulatorHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? "127.0.0.1";
const emulatorPort = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? "5001");

// Function to connect to emulators (for manual calls if needed)
// This ensures emulators are connected even if the module was cached
export function connectToEmulators(): void {
  console.log("%c[Firebase Client] Ensuring emulator connections...", "color: #9C27B0; font-weight: bold");
  connectToEmulatorsInternal();
  
  // Also ensure Firestore is using the correct database ID
  if (typeof window !== "undefined" && shouldUseEmulatorsNow()) {
    const dbId = getDatabaseId();
    console.log(`%c[Firebase Client] Verified database ID: ${dbId}`, "color: #2196F3");
  }
}

const getClientFunctions = (): Functions | null => {
  if (!cachedFunctions) {
    cachedFunctions = globalCache.functions ?? getFunctions(app, "us-central1");
    globalCache.functions = cachedFunctions;
  }

  if (typeof window === "undefined") {
    return cachedFunctions;
  }

  if (!hasConnectedToEmulator && cachedFunctions) {
    const hostname = window.location.hostname;
    if (shouldUseEmulator(hostname)) {
      try {
        console.info(`Connecting to functions emulator at ${emulatorHost}:${emulatorPort}`);
        if (!globalCache.functionsEmulatorConnected) {
          connectFunctionsEmulator(cachedFunctions, emulatorHost, emulatorPort);
          globalCache.functionsEmulatorConnected = true;
        }
        console.info("Connected to functions emulator successfully");
      } catch (error) {
        console.error("Failed to connect to functions emulator", error);
      } finally {
        hasConnectedToEmulator = true;
      }
    }
  }

  return cachedFunctions;
};

// Initialize messaging (lazy-loaded)
type MessagingInstance = Awaited<ReturnType<typeof import("firebase/messaging").getMessaging>> | null;
let messagingInstance: MessagingInstance = null;
let messagingPromise: Promise<MessagingInstance> | null = null;

async function getMessagingInstance(): Promise<MessagingInstance> {
  if (typeof window === "undefined") {
    return null;
  }

  if (messagingInstance) {
    return messagingInstance;
  }

  if (messagingPromise) {
    return messagingPromise;
  }

  messagingPromise = (async () => {
    try {
      const { getMessaging, isSupported } = await import("firebase/messaging");
      const supported = await isSupported();
      if (!supported) {
        return null;
      }
      messagingInstance = getMessaging(app);
      return messagingInstance;
    } catch (error) {
      console.error("[Firebase Client] Failed to initialize messaging:", error);
      return null;
    }
  })();

  return messagingPromise;
}

export { analytics, app, appleProvider, auth, db, getAnalyticsInstance, getClientFunctions, getMessagingInstance, googleProvider, storage };

